/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectors } from '@twaice-fe/frontend/shared/store';
import jwtDecode from 'jwt-decode';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { userTrackingActions } from 'libs/frontend/shared/store/src/actions';
import mixpanel from 'mixpanel-browser';
import { distinctUntilChanged, filter, from, switchMap, tap } from 'rxjs';
import { AuthService } from './auth.service';
import { CookieConsentService } from './cookie-consent.service';
import { HubspotWindow } from './user-tracking.types';

const { authSelectors } = selectors;

const HUBSPOT_SCRIPT_ID = 'hs-script-loader';

@Injectable({
  providedIn: 'root',
})
export class UserTrackingService {
  constructor(
    private authService: AuthService,
    private cookieConsentService: CookieConsentService,
    private store: Store
  ) {}

  setupMixpanelTracking(token: string): void {
    if (!token) return;

    mixpanel.init(token, {
      debug: false,
      track_pageview: 'url-with-path',
      persistence: 'localStorage',
      // this is an output of the OpsStage/MixpanelProxyStack stack.
      // As this value should not change it's hardcoded here but could be injected dynamically as well
      api_host: 'https://track.ops.twaice.ai',
      ignore_dnt: true,
    });

    this.cookieConsentService
      .getChoices()
      .pipe(distinctUntilChanged())
      .subscribe((optIn) => {
        if (optIn === false) {
          mixpanel.opt_out_tracking();
          return;
        }

        if (!mixpanel.has_opted_in_tracking()) {
          mixpanel.opt_in_tracking();
        }
        this.store.dispatch(userTrackingActions.mixpanelSetup());
      });
  }

  setupHubspotIntegration(hubspotPortalId: string): void {
    if (!hubspotPortalId) {
      console.log('[HubSpot] chat disabled: no hubspotPortalId configured for this environment');
      return;
    }

    console.log('[HubSpot] waiting for a logged-in user, portal', hubspotPortalId);
    const hubspotWindow = window as HubspotWindow;
    this.store
      .select(authSelectors.getUser)
      .pipe(
        filter((user) => !!user),
        switchMap(() => from(this.authService.getIdToken())),
        tap((token) => {
          // only present for users with a HubSpot contact -- everyone else gets no chat widget
          const hubspotVisitorToken = jwtDecode(token)?.['hubspot_visitor_token'];
          if (!hubspotVisitorToken) {
            console.log('[HubSpot] chat not shown: id token has no hubspot_visitor_token claim');
            hubspotWindow.HubSpotConversations?.widget.remove();
            return;
          }

          console.log('[HubSpot] hubspot_visitor_token found, loading chat for', this.authService.getUsername());
          this.loadHubspotWidget(hubspotWindow, hubspotPortalId, this.authService.getUsername(), hubspotVisitorToken);
        }),
        switchMap(() => this.authService.getUserObservable()),
        tap((user) => {
          if (user) return;

          // end session if user logs out
          console.log('[HubSpot] user logged out, removing chat');
          hubspotWindow.HubSpotConversations?.widget.remove();
          hubspotWindow.HubSpotConversations?.clear();
        })
      )
      .subscribe();
  }

  private loadHubspotWidget(
    hubspotWindow: HubspotWindow,
    hubspotPortalId: string,
    email: string | undefined,
    hubspotVisitorToken: string
  ): void {
    const identifyAndLoad = () => {
      hubspotWindow.hsConversationsSettings = {
        loadImmediately: false,
        identificationEmail: email,
        identificationToken: hubspotVisitorToken,
      };
      if (hubspotWindow.HubSpotConversations) {
        console.log('[HubSpot] visitor identified, calling widget.load()');
        hubspotWindow.HubSpotConversations.widget.load();
        return;
      }
      console.log('[HubSpot] visitor identified, widget.load() queued until the conversations SDK is ready');
      hubspotWindow.hsConversationsOnReady = [
        ...(hubspotWindow.hsConversationsOnReady ?? []),
        () => {
          console.log('[HubSpot] conversations SDK ready, calling widget.load()');
          hubspotWindow.HubSpotConversations?.widget.load();
        },
      ];
    };

    if (document.getElementById(HUBSPOT_SCRIPT_ID)) {
      console.log('[HubSpot] loader script already on the page, re-identifying visitor');
      identifyAndLoad();
      return;
    }

    // taken from: https://developers.hubspot.com/docs/api/conversation/chat-widget-sdk
    hubspotWindow.hsConversationsSettings = { loadImmediately: false };
    const script = document.createElement('script');
    script.id = HUBSPOT_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = `https://js.hs-scripts.com/${hubspotPortalId}.js`;
    script.onload = () => {
      console.log('[HubSpot] loader script loaded');
      identifyAndLoad();
    };
    // e.g. blocked by an ad blocker or the network
    script.onerror = () => console.error('[HubSpot] chat not shown: failed to load', script.src);
    console.log('[HubSpot] injecting loader script', script.src);
    document.body.appendChild(script);
  }
}
